import React, { useMemo } from 'react'

import { useScript } from 'hoofd'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const Creation = () => {
  const howToData = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Create a Presentation on Guild',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Open the Command Palette',
            text: 'Click the Command Palette icon in the top navigation bar to open the Command Palette.',
          },
          {
            '@type': 'HowToStep',
            name: 'Select Create a Presentation',
            text: 'Choose "Create a Presentation" from the quick actions, or type "create presentation" to find it.',
          },
          {
            '@type': 'HowToStep',
            name: 'Enter the title and description',
            text: 'Give your Presentation a title and write a description covering the topic.',
          },
          {
            '@type': 'HowToStep',
            name: 'Assign a presenter',
            text: 'Choose yourself, an existing Member, or add an external presenter by name and email.',
          },
          {
            '@type': 'HowToStep',
            name: 'Add a video URL',
            text: 'Optionally paste a YouTube or Vimeo link to embed the recording.',
          },
          {
            '@type': 'HowToStep',
            name: 'Save your Presentation',
            text: 'Click Save to publish the Presentation. It will appear on your profile and can be linked to Events and Guilds.',
          },
        ],
      }),
    []
  )
  useScript({ type: 'application/ld+json', text: howToData })

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          Presentations are created through the{' '}
          <Paragraph size='$5' fontWeight='$7'>Command Palette</Paragraph> — a
          search and action interface accessible from the top navigation bar. Any Organizer or Editor can create a
          Presentation by following these steps.
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='steps' title='Steps'>
          <DocStep
            number={1}
            title='Open the Command Palette'
            description='Click the Command Palette icon in the top navigation bar. The Command Palette opens with a list of quick actions and a search field.'
            screenshot={{
              src: 'docs/getting-started/command-menu-open.png',
              alt: 'Command Palette open showing quick actions including Create a Presentation',
              caption: 'The Command Palette with quick actions including Create a Presentation',
            }}
          />
          <DocStep
            number={2}
            title='Select Create a Presentation'
            description='Choose "Create a Presentation" from the quick actions list. You can also type "create presentation" or natural language like "add a new talk" into the search field to find it.'
          />
          <DocStep
            number={3}
            title='Enter the title and description'
            description='Give your Presentation a clear title and write a description covering what the talk is about. This helps attendees and future viewers understand the content at a glance.'
          />
          <DocStep
            number={4}
            title='Assign a presenter'
            description='Choose yourself, select an existing Member, or add an external presenter by entering their name and email address. External presenters will receive an invitation to claim the Presentation.'
          />
          <DocStep
            number={5}
            title='Add a video URL (optional)'
            description='Paste a YouTube or Vimeo link to embed the recording directly on the Presentation page. The video player appears at the top of the page for easy viewing.'
          />
          <DocStep
            number={6}
            title='Save your Presentation'
            description='Click Save to publish the Presentation. It will appear on your profile and can be linked to Events and Guilds from their respective settings pages.'
          />
        </DocSection>
      </Card>
    </>
  )
}

export default Creation
